/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { useState } from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import Tab from '@mui/material/Tab';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import { InviteeResponse, useEventInvitees, useGetEvent } from 'api';
import {
  Alert,
  ButtonV2,
  TextV2,
} from '@insights-ltd/design-library/components';
import {
  dateInPastForZone,
  toLocaleString,
} from '@insights-ltd/design-library/utils';
import { queryStatus } from 'utils/queryStatus';
import Breadcrumbs from 'components/Breadcrumbs';
import FullHeight from 'components/FullHeight';
import { FullScreenError, FullScreenSpinner } from 'components/FullScreen';
import useIsPermitted from 'components/hooks/useIsPermitted';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { Navigate, useParams } from 'react-router';
import Notification from 'components/Notification';
import { isDFCExperience, isIdtlExperience } from 'domain/event';
import { spacingSizeMap } from '@insights-ltd/design-library/themes';
import { backButtonStyles } from 'components/layout/BreadcrumbLayout/BreadcrumbLayout';
import BackButton from 'components/BackButton/BackButton';
import ActionRequiredList from './ActionRequiredList/ActionRequiredList';
import AddLearnerForm from './AddLearnerForm';
import BulkUpload from './BulkUpload';
import LearnersList from './LearnersList';
import {
  AddLearnersToTeamModal,
  getPreviousProfileInvitees,
} from './EventLearners.helper';

const TabViewValue = {
  AddLearnersTab: 'LEARNERS',
  BulkUploadTab: 'UPLOAD',
};

const EventLearners = () => {
  const { t } = useTranslation();
  const { eventId } = useParams<{ eventId: string }>();
  const [tabbedView, setTabbedView] = useState(TabViewValue.AddLearnersTab);
  const [isAddToTeamOpen, setIsAddToTeamOpen] = useState(false);

  const { status: eventStatus, data: event } = useGetEvent(eventId || '');

  const { status: inviteesStatus, data: invitees = [] } = useEventInvitees(
    eventId || '',
  );
  const previousProfileInvitees = getPreviousProfileInvitees(invitees);
  const { status: permissionStatus, isPermitted: readExpiredIsPermitted } =
    useIsPermitted([
      {
        action: 'Experience_ReadExpired',
        scope: 'Local',
      },
    ]);

  const responseStatus = queryStatus(
    eventStatus,
    inviteesStatus,
    permissionStatus,
  );

  if (responseStatus === 'pending') {
    return <FullScreenSpinner message={t('ui.event-management.loading')} />;
  }
  if (responseStatus === 'error') {
    return (
      <FullScreenError
        message={t('ui.event-management.events.overview.error-loading-event')}
      />
    );
  }

  const pastExtendedEndDate: boolean = dateInPastForZone(
    event!.extendedEndDate,
    event!.timezone,
  );

  if (
    (pastExtendedEndDate && !readExpiredIsPermitted) ||
    isDFCExperience(event!.eventType)
  ) {
    return <Navigate to={`/experiences/${event!.id}`} />;
  }

  const isCancelled: boolean = event?.eventStatus === 'CANCELLED';
  const pastEndDate: boolean = dateInPastForZone(
    event!.endsAt,
    event!.timezone,
  );

  const isWithinGracePeriod: boolean = pastEndDate && !pastExtendedEndDate;

  const isReadonly =
    (isCancelled || pastExtendedEndDate) && readExpiredIsPermitted;

  const expDateText = toLocaleString(
    event!.startsAt,
    event!.timezone,
    event!.dialect,
  );

  const parentLink = `/experiences/${eventId}`;

  const currentProfileInvitees: InviteeResponse[] = invitees.filter(
    (invitee) =>
      invitee.profile?.status !== 'AVAILABLE_FOR_REUSE' ||
      invitee.profile.reusePreference !== 'NONE',
  );

  return (
    <>
      <Helmet>
        <title>
          {t('ui.event-management.title.learners', { eventName: event!.name })}
        </title>
      </Helmet>
      <FullHeight backgroundColor="white">
        <Container maxWidth="lg">
          <Box
            sx={{
              py: (theme) => theme.spacing(spacingSizeMap.M),
            }}
          >
            <Breadcrumbs
              crumbs={{
                '/': t('ui.event-management.events.nav.home'),
                '/experiences': t('ui.event-management.events.title'),
                [`/experiences/${eventId}`]: event!.name,
              }}
              activeText={
                isReadonly
                  ? t('ui.event-management.events.summary.view-learners')
                  : t('ui.event-management.events.summary.manage-learners')
              }
            />
          </Box>
          <Box
            sx={{
              mb: (theme) => theme.spacing(spacingSizeMap.S),
            }}
          >
            <BackButton
              parentLink={parentLink}
              i18nKey="ui.event-management.events.add-learners-and-contributors.learner.back-to-experience-overview"
              sx={backButtonStyles}
            />
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <TextV2 component="h1" variant="h2">
                {isReadonly
                  ? t('ui.event-management.events.add-learners.read-only.title')
                  : t('ui.event-management.events.add-learners.title')}
              </TextV2>
              {!isCancelled && (
                <ButtonV2
                  id="event-add-to-team-button"
                  sx={{ paddingRight: 2 }}
                  variant="outlined"
                  buttonText={t('ui.event-management.add-to-team.button')}
                  onClick={() => setIsAddToTeamOpen(true)}
                />
              )}
            </Box>
          </Box>

          {pastExtendedEndDate || isCancelled ? (
            <>
              <Box
                sx={{
                  mb: (theme) => theme.spacing(spacingSizeMap.S),
                }}
              >
                {isWithinGracePeriod ? (
                  <TextV2>
                    {t(
                      'ui.event-management.events.add-learners.read-only.sub-title',
                      { expEndDate: expDateText },
                    )}
                  </TextV2>
                ) : (
                  <Alert severity="warning">
                    {t(
                      'ui.event-management.events.add-learners.read-only.archived.sub-title',
                    )}
                  </Alert>
                )}
              </Box>
              <LearnersList
                isPastExperience={pastExtendedEndDate}
                invitees={pastEndDate ? currentProfileInvitees : invitees}
                event={event!}
              />
            </>
          ) : (
            <>
              {' '}
              <Box
                sx={{
                  mb: (theme) => theme.spacing(spacingSizeMap.S),
                }}
              >
                <TextV2 color="textSecondary">
                  {t('ui.event-management.events.add-learners.sub-title')}
                </TextV2>
              </Box>
              {isIdtlExperience(event?.eventType || '') && (
                <Container sx={{ margin: '0', padding: '0' }} maxWidth="md">
                  <Notification
                    message={t(
                      'ui.event-management.events.learners.notification.get-idtl-discovery-purchase',
                    )}
                  />
                </Container>
              )}
              <TabContext value={tabbedView}>
                <TabList
                  onChange={(_, value) => setTabbedView(value)}
                  textColor="primary"
                  indicatorColor="primary"
                  aria-label={t(
                    'ui.event-management.events.add-learners.tab.label',
                  )}
                >
                  <Tab
                    label={t(
                      'ui.event-management.events.add-learners.tab.learners',
                    )}
                    value={TabViewValue.AddLearnersTab}
                  />
                  <Tab
                    label={t(
                      'ui.event-management.events.add-learners.tab.upload',
                    )}
                    value={TabViewValue.BulkUploadTab}
                  />
                </TabList>
                <TabPanel
                  value={TabViewValue.AddLearnersTab}
                  aria-label={t(
                    'ui.event-management.events.add-learners.tab.learners',
                  )}
                  sx={{ padding: '0' }}
                >
                  <Box
                    sx={{
                      mt: (theme) => theme.spacing(spacingSizeMap.L),
                    }}
                  />
                  <Box
                    sx={{
                      mb: (theme) => theme.spacing(spacingSizeMap.S),
                    }}
                  >
                    <TextV2 component="h2" variant="h3">
                      {t('ui.event-management.events.add-learners.add-learner')}
                    </TextV2>
                  </Box>
                  <AddLearnerForm eventId={eventId || ''} />
                </TabPanel>
                <TabPanel
                  value={TabViewValue.BulkUploadTab}
                  aria-label={t(
                    'ui.event-management.events.add-learners.tab.upload',
                  )}
                  sx={{ padding: '0' }}
                >
                  <BulkUpload eventId={eventId || ''} />
                </TabPanel>
                {previousProfileInvitees.length > 0 ||
                currentProfileInvitees.length > 0 ? (
                  <Divider
                    sx={(theme) => ({
                      marginTop: theme.spacing(spacingSizeMap.L),
                      marginBottom: theme.spacing(spacingSizeMap.L),
                    })}
                  />
                ) : null}
                {previousProfileInvitees.length > 0 && (
                  <ActionRequiredList
                    isPastExperience={pastExtendedEndDate}
                    invitees={previousProfileInvitees}
                    eventId={eventId || ''}
                    eventTimezone={event!.timezone}
                    eventDialect={event!.dialect}
                  />
                )}
                <LearnersList
                  isPastExperience={pastExtendedEndDate}
                  invitees={currentProfileInvitees}
                  event={event!}
                />
              </TabContext>
            </>
          )}
        </Container>
      </FullHeight>
      {event && (
        <AddLearnersToTeamModal
          invitees={invitees}
          event={event}
          isAddToTeamOpen={isAddToTeamOpen}
          setIsAddToTeamOpen={setIsAddToTeamOpen}
        />
      )}
    </>
  );
};

export default EventLearners;
/* eslint-enable @typescript-eslint/no-non-null-assertion */
